import React from 'react';
import { ScrollView } from 'react-native';
import { useTheme } from 'react-native-paper';
import { t } from '@lingui/macro';
import { useScrollToTop } from '@react-navigation/native';

import Container from '@src/components/Container';
import Cover from '@src/components/Cover';
import RefreshControl from '@src/components/RefreshControl';
import Separator from '@src/components/Separator';
import TourSummeryCard from '@src/components/TourSummeryCard';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { DynamicPageItemAPIProduce } from '@src/enums';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import useDebounce from '@src/hooks/useDebounce';
import useHandleEntityPress from '@src/hooks/useHandleEntityPress';
import useTourSearch from '@src/hooks/useTourSearch';
import { i18n } from '@src/i18n';

import { ImageIcon, List, SearchBar, SearchContainer } from './styled';
import TourItems from './TourItems';
import { Props } from './types';
import useSearchTour from './useSearchTour';
import useTours from './useTours';

const Screen: React.FC<Props> = () => {
  const tourHome = useTours();
  const { search, setSearch } = useTourSearch();
  const q = useDebounce(search, 300);
  const searchTour = useSearchTour(q);
  const ref = React.useRef(null);
  const handleScroll = useAnimatedHeader();
  const handleEntityPress = useHandleEntityPress();
  useScrollToTop(ref);

  const theme = useTheme();
  const handlePress = React.useCallback(() => {
    setSearch(search);
  }, [search, setSearch]);

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <SearchContainer>
          <SearchBar
            onIconPress={handlePress}
            onSubmitEditing={handlePress}
            value={search}
            inputStyle={{
              fontFamily: theme.fonts.regular.fontFamily,
              fontWeight: 'normal',
              fontSize: 16,
            }}
            icon={({ size, color }) => (
              <ImageIcon
                source={require('@src/assets/images/search.png')}
                style={{
                  width: 24,
                  height: 24,
                  tintColor: theme.colors.onSurface,
                }}
              />
            )}
            onChangeText={setSearch}
            placeholder={i18n._(t`Search...`)}
          />
        </SearchContainer>
        {searchTour.data.length > 0 ? (
          <List
            data={searchTour.data}
            renderItem={({ item }) => <TourSummeryCard item={item} />}
            onScroll={handleScroll}
            onEndReached={searchTour.handleEndReached}
            refreshControl={
              <RefreshControl
                refreshing={searchTour.refreshing}
                onRefresh={searchTour.handleRefresh}
              />
            }
          />
        ) : (
          <ScrollView scrollEventThrottle={16} onScroll={handleScroll}>
            {tourHome.map((item, index) => {
              if (item.type === 'COVER') {
                const data = item.data;
                return (
                  <Cover
                    key={index}
                    source={data.cover.source}
                    onPress={handleEntityPress(data)}
                  />
                );
              } else if (item.type === 'API') {
                const data = item.data;
                if (data.produces === DynamicPageItemAPIProduce.Tour) {
                  return (
                    <>
                      <TourItems key={`tours-${index}`} data={data} />
                      <Separator key={`separator-${index}`} />
                    </>
                  );
                } else {
                  return null;
                }
              } else {
                return null;
              }
            })}
          </ScrollView>
        )}
      </Container>
    </>
  );
};

Screen.displayName = 'Tours';

export default Screen;
